// @flow
import React, { Fragment, createRef } from 'react';
import NextApp from 'next/app';
import Head from 'next/head';
import { throttle } from 'lodash';
import { b2cPagesService, constants, httpHeaders } from '@clubmed/utils';
import URI from 'urijs';
import { type Store } from 'react-redux';

import config from 'Config';
import NextRoutes from 'Routes/nextRoutes';
import { getSiteSection } from 'Tracking/utils';
import { getName } from 'Utils/request/requestDictionary';
import { isSeller } from 'Utils/seller/sellerUtils';
import { Layout } from 'Components/layouts/layout/Layout.jsx';
import { fetchLayoutConfig } from 'Components/layouts/layout/Layout.query';
import { setLocale as setI18nLocale, addTranslationKeys, getAll } from 'Src/i18n';
import { getCurrentUri, isStrictlyDevelopmentEnv } from 'Utils/context/contextUtils';
import { handleInvalidToken } from 'Utils/ple-utils/handleInvalidToken';
import { getOrCreateStore } from 'Store/withReduxStore';
import {
  createAppContextFromReq,
  createAppContextFromProps,
} from 'Server/context/createAppContextFromReq';
import { getAppContext, saveAppContext } from 'Context/createOrUpdateAppContext';
import { createRouterLocation, updateShallowQueryParams } from 'Client/router/createRouterLocation';
import { getKids } from 'Store/trip-information/storeHelper';
import { PriorityThirdPartyScripts } from 'Components/third-party/PriorityThirdPartyScripts';
import { ThirdPartyScripts } from 'Components/third-party/ThirdPartyScripts';
import { EasyTesting } from 'Components/utils/easy-testing/index.jsx';
import { Loading } from 'Components/widgets/loading/Loading.jsx';
import { ChatbotContainer } from 'Client/chatbot/ChatbotContainer.jsx';
import { ErrorBoundary } from 'Context/ErrorBoundary';
import NotFoundView from 'Views/errors/not-found/NotFoundView.jsx';
import { HeadInfos } from 'Components/layouts/head-info/HeadInfos';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { ThreeSixtyContainer } from 'Components/widgets/three-sixty/ThreeSixtyContainer.jsx';
import { start as handleErrors } from 'Errors/clientErrorHandler';
import {
  LOCAL_STORAGE_STATE_KEY,
  getInitialReduxState,
  stateFromCookies,
} from 'Store/getInitialReduxState.client';
import { SYNC_WITH_LOCATION } from 'Store/constants';
import { RouteChangeListener } from 'App/RouteChangeListener';
import { AppProviders } from 'App/AppProviders';
import { setInLocalStorage } from 'Client/local-storage-api';
import { invariant } from 'Utils/invariant/invariant';
import { STATUS_CODE_200, STATUS_CODE_404 } from 'Utils/status-code/statusCode.constants';
import { ToastContainer } from 'Components/frameworks/toast/Toast.container';
import { COOKIE_WARNING_LOCALSTORAGE_KEY } from 'Components/widgets/cookie-warning/constants';
import { CMTA } from 'Components/widgets/cmta/CMTA';

type Props = {
  appContext: AppContext,
  pageContext: PageContext,
  i18nData: CMSContent,
  layoutData: {
    config: CMSContent,
    legalInformations: Array<{
      id: number,
      description: string,
    }>,
    newsletters: Array<{
      id: string,
      description: string,
    }>,
  },
  initialReduxState: ReduxState,
  statusCode: number,
};

// $FlowFixMe issue with next typings
class App extends NextApp<Props> {
  static async getInitialProps({ Component, ctx }: *) {
    const { req, res, asPath, query } = ctx;

    const appContext = req ? createAppContextFromReq(req) : getAppContext();

    // If no appContext, we reload the page. It's occurring when navigating back from an error layer
    if (!appContext && typeof window !== 'undefined' && isStrictlyDevelopmentEnv(config)) {
      window.location.reload();
    }

    let pageProps = {};

    // Get or Create the store with initialState from cookies
    // This allows you to set a custom default initialState
    const reduxStore = getOrCreateStore(stateFromCookies());

    if (Component.requireAuthentication) {
      const currentUri = req ? getCurrentUri(req) : new URI(window.location.href);
      handleInvalidToken({
        appContext,
        currentUri,
        res,
        userInfo: userInfoSelector(reduxStore.getState()),
      });
    }

    // Provide the store to getInitialProps of pages
    ctx.reduxStore = reduxStore;

    const { isUserQuickLogged, originalUrl } = appContext;
    const { pageId } = Component;
    const pageType = Component.pageType || Component.pageId || 'none';
    const location = createRouterLocation({
      asPath,
      query,
      originalUrl,
    });

    // There are two possibilities for the pageContext
    // - it's created in the getInitialProps of the page (DynamicPage.jsx)
    // - it's static, the properties depending on the pages can be statically resolved (e.g. pageId)
    // We create it before the getInitialProps and pass it to the getInitialProps because some
    // components might need it, like ThemeView which does render lines
    const staticPageContext: PageContext = {
      pageId,
      pageTitle: constants.WEBSITE_NAME,
      pageDescription: '',
      // usually we don't care about the statusCode in the pageContext, since a 404/500 will
      // directly route us to a specific branch
      statusCode: STATUS_CODE_200,
      hrefLang: [],
      pageMeta: {},
      pageType: pageType || pageId || 'none',
      siteSection: getSiteSection({ isUserQuickLogged, pageType }),
    };

    // Call here, await later to make this call and the getInitialProps one parallels
    const layoutPromise = req ? fetchLayoutConfig(appContext) : Promise.resolve({});

    if (Component.getInitialProps) {
      try {
        pageProps = await Component.getInitialProps(
          ({
            ...ctx,
            appContext,
            pageContext: staticPageContext,
            location,
          }: GetInitialPropsContext<>),
        );
      } catch (err) {
        pageProps = err;
      }

      if (pageProps.statusCode) {
        if (pageProps.statusCode === STATUS_CODE_404) {
          if (typeof window === 'undefined') {
            try {
              const { getRedirect, RULES } = require('@clubmed/redirects');
              const { status, url } = await getRedirect(config.get(), req, [
                RULES.B2C_CMS_CONTENT,
                RULES.GLOBAL_B2C,
                RULES.PRODUCT_SHORT_LINKS,
                RULES.LEGACY_B2C_NOT_FOUND,
              ]);
              return res.redirect(status, url);
            } catch (e) {
              pageProps = {
                ...pageProps,
                ...(await NotFoundView.getInitialProps({ appContext })),
              };
            }
          } else {
            pageProps = {
              ...pageProps,
              ...(await NotFoundView.getInitialProps({ appContext })),
            };
          }
        }
      }
    }

    let pageContext: PageContext;
    if (pageProps.pageContext) {
      pageContext = pageProps.pageContext;
    } else {
      invariant(
        pageId,
        `The page corresponding to the url ${asPath} doesn't have a pageId. This is an error.`,
      );

      pageContext = {
        ...staticPageContext,
        statusCode: pageProps.statusCode || staticPageContext.statusCode,
      };
    }

    // Sync store with url, works fine on SSR to have
    // last version of state before rendering on frontend.
    if (typeof window === 'undefined') {
      ctx.reduxStore.dispatch({
        type: SYNC_WITH_LOCATION,
        payload: {
          location,
          pageType: staticPageContext.pageType,
        },
      });
    }

    const layoutData = await layoutPromise;

    const appContextLight: $Shape<AppContext> = {
      locale: appContext.locale,
      originalUrl: appContext.originalUrl,
      cookies: {
        // TODO is_mobile
      },
    };

    if (typeof window === 'undefined') {
      if (req.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY]) {
        appContextLight.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY] =
          req.cookies[COOKIE_WARNING_LOCALSTORAGE_KEY];
      }

      res.status(pageContext.statusCode);

      // We set this header to trace the type of request in the logs
      res.set(
        httpHeaders.X_REQUEST_NAME,
        getName(['PAGE', (pageProps.pageId || staticPageContext.pageType || '').toUpperCase()]),
      );
    }

    return {
      appContext: appContextLight,
      pageContext,
      i18nData: getAll(),
      pageProps,
      layoutData,
      disableHeader:
        isSeller(appContext) ||
        query.disableLayout === 'true' ||
        (typeof Component.disableHeader === 'function'
          ? Component.disableHeader(appContext)
          : Component.disableHeader),
      disableFooter:
        isSeller(appContext) ||
        query.disableLayout === 'true' ||
        (typeof Component.disableFooter === 'function'
          ? Component.disableHeader(appContext)
          : Component.disableFooter),
      location,
      initialReduxState: reduxStore.getState(),
      statusCode: pageContext.statusCode,
    };
  }

  appContext: AppContext;
  pageContext: PageContext;
  reduxStore: Store<ReduxState>;

  toastRef: { current: ?HTMLElement };

  constructor(props: Props) {
    // $FlowFixMe issue with next typings
    super(props);

    this.toastRef = createRef();

    this.appContext =
      typeof window !== 'undefined'
        ? saveAppContext(props.appContext)
        : createAppContextFromProps(props.appContext);

    // Get or Create the store with initialState from cookies
    this.reduxStore = getOrCreateStore(
      typeof window !== 'undefined'
        ? getInitialReduxState(props.initialReduxState, this.appContext.locale)
        : props.initialReduxState,
    );

    setI18nLocale(this.appContext.locale.slice(0, 2));

    // First time on the render it comes from the props, then it's there and won't have to be refeched
    this.layoutData = props.layoutData || this.layoutData;
  }

  // eslint-disable-next-line class-methods-use-this
  componentDidMount() {
    handleErrors();

    NextRoutes.Router.events.on('routeChangeComplete', () => {
      // as shallow navigation may happen, ensure the store is synced with location.
      this.reduxStore.dispatch({
        type: SYNC_WITH_LOCATION,
        payload: {
          location: this.props.location,
          pageType: this.props.pageContext.pageType,
        },
      });
    });

    // https://github.com/zeit/next.js/issues/7814
    // Navigating back (clicking on the browser back button) triggers a getInitialProps every time
    // This makes change when changing of page. But we often do shallow-routing on a page to show a
    // layer (or to persist things in the url), but don' trigger getInitialProps
    // Sadly, back/forward means that we retrigger the getInitialProps, show the loader, etc.
    // So with this snippet we force same page navigation as shallow (since different pages skip
    // shallow routing)
    NextRoutes.Router.beforePopState(({ options }) => {
      // this.props.location has not been updated yet, so we can check and prevent shallow navigation
      // between two different dynamic pages
      if (this.props.location.pathname === new URI(window.location.href).pathname()) {
        options.shallow = true;
      }

      return true;
    });

    // Sync the state with the local storage
    this.reduxStore.subscribe(
      throttle(() => {
        const state = this.reduxStore.getState();
        const persistedStore = JSON.stringify({
          tripInformation: { kids: getKids(state) },
          seller: state.seller,
        });
        setInLocalStorage(`${LOCAL_STORAGE_STATE_KEY}-${this.appContext.locale}`, persistedStore);
      }, 1000),
    );
  }

  render() {
    const {
      Component,
      pageContext,
      pageProps,
      i18nData,
      disableFooter,
      disableHeader,
      location: unUpdatedLocation,
    } = this.props;

    // With shallow routing, the page is rerendered and the query params are updated, but
    // getInitialProps is not called. So we need to manually update the queryParams
    const location = updateShallowQueryParams({
      location: unUpdatedLocation,
      href: typeof window !== 'undefined' ? window.location.href : null,
    });

    const routerContextValue = {
      history: NextRoutes.Router,
      location,
    };

    if (typeof window !== 'undefined') {
      addTranslationKeys(i18nData);
    }

    return (
      <Fragment>
        <ErrorBoundary useNativeErrorHandling>
          <Head>
            <title key="title">{pageContext.pageTitle}</title>
          </Head>
          <AppProviders
            appContext={this.appContext}
            pageContext={pageContext}
            reduxStore={this.reduxStore}
            routerContextValue={routerContextValue}
            toastRef={this.toastRef}
          >
            <RouteChangeListener />
            <HeadInfos />
            <PriorityThirdPartyScripts key="PriorityThirdPartyScripts" />
            <ThreeSixtyContainer />
            <CMTA />
            <Loading />
            <ErrorBoundary>
              <ChatbotContainer />
            </ErrorBoundary>
            <EasyTesting />
            <div
              id={pageContext.pageId || 'pageWrapper'}
              // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
              data-cs-override-id="page"
            >
              <Layout
                {...this.layoutData}
                disableHeader={disableHeader}
                disableFooter={disableFooter}
                isMicePage={b2cPagesService.isMicePage(pageContext.pageType)}
              >
                {pageProps.statusCode === 404 ? (
                  <NotFoundView {...pageProps} />
                ) : (
                  <Component {...pageProps} />
                )}
              </Layout>
            </div>
            <ToastContainer ref={this.toastRef} />
            <ThirdPartyScripts key="ThirdPartyScripts" />
          </AppProviders>
        </ErrorBoundary>
      </Fragment>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default App;
